/**
 * Per-fabric detail card on the PDP.
 *
 * Sits below the FabricFeatures icon row (Buttery Soft / Stretch /
 * Breathable / Quick Dry) and gives customers structured copy about
 * the specific fabric the product uses: tagline → 2-3 bullet
 * properties → recommended use cases. Same shape across all 5 Diolo
 * fabrics so the layout stays consistent.
 *
 * Renders nothing when:
 *   - the product has no `fabric` field set (likely accessory), or
 *   - the value doesn't map to a known fabric in FABRIC_INFO.
 *
 * Author's content: see app/utils/fabricInfo.ts. Pulled originally
 * from shop.diolo.com/collections/eql<fabric> (2024 copy).
 */
import type { Locale } from "~/utils/locale";
import { getFabricInfo } from "~/utils/fabricInfo";

interface Props {
  fabric: string | null | undefined;
  locale: Locale;
  /**
   * Visual variant.
   *  - "default": legacy standalone card with top border + outer margin —
   *    used historically when this component sat OUTSIDE the accordion stack.
   *  - "accordion": nested inside an `<AritziaAccordion>` content area —
   *    drops the outer border/margin (the accordion provides those) and
   *    hides the duplicated fabric name heading (the accordion trigger
   *    already says "Fabric / Chất liệu").
   * Default is "default" so existing usages remain pixel-stable.
   */
  variant?: "default" | "accordion";
}

export function FabricDetails({ fabric, locale, variant = "default" }: Props) {
  const info = getFabricInfo(fabric);
  if (!info) return null;

  const tagline = locale === "vi" ? info.taglineVi : info.taglineEn;
  const bullets = locale === "vi" ? info.bulletsVi : info.bulletsEn;
  const bestFor = locale === "vi" ? info.bestForVi : info.bestForEn;
  const bestForLabel = locale === "vi" ? "Phù hợp nhất cho" : "Best for";
  const isAccordion = variant === "accordion";

  return (
    <section
      className={isAccordion ? "" : "mt-6 border-t border-[#e0e0e0] pt-5"}
      aria-label={locale === "vi" ? "Chi tiết chất vải" : "Fabric details"}
    >
      {/* Fabric brand-mark heading — kept in accordion variant too because
          customers know fabrics by name (DioloRIB™, DioloFLO™, etc.) and the
          accordion title is just the category word "Fabric". Render it
          smaller in the accordion variant so the trigger remains the
          visual anchor. */}
      <h3
        className={
          isAccordion
            ? "text-[11px] uppercase tracking-[0.15em] text-black font-medium"
            : "text-[11px] uppercase tracking-[0.15em] text-black font-medium"
        }
      >
        {info.name}
      </h3>
      <p className="mt-1 text-xs text-[#555] italic">{tagline}</p>
      <ul className="mt-3 space-y-1.5 text-xs text-[#555] leading-relaxed">
        {bullets.map((b) => (
          <li key={b} className="flex items-start gap-2">
            <span className="select-none mt-1.5 inline-block w-1 h-1 rounded-full bg-black flex-shrink-0" />
            <span>{b}</span>
          </li>
        ))}
      </ul>
      <p className="mt-3 text-[11px] text-[#767676]">
        <span className="text-black">{bestForLabel}:</span> {bestFor}
      </p>
    </section>
  );
}
